import { useState, useCallback } from 'react';

import { type UserSettingsForm } from './UserSettings';

type UseHandednessConfirmationArgs = {
    currentLeftHanded: boolean;
    onConfirmSave: (form: UserSettingsForm, createNewActivity?: boolean) => Promise<void>;
    onCreateNewActivity?: () => Promise<number>;
};

export function useHandednessConfirmation({
    currentLeftHanded,
    onConfirmSave,
    onCreateNewActivity,
}: UseHandednessConfirmationArgs) {
    const [pendingForm, setPendingForm] = useState<UserSettingsForm | null>(null);

    const handleSave = useCallback(
        async (form: UserSettingsForm) => {
            const changed = form.leftHanded !== currentLeftHanded;

            if (changed && onCreateNewActivity) {
                setPendingForm(form);
                return;
            }

            await onConfirmSave(form);
        },
        [currentLeftHanded, onConfirmSave, onCreateNewActivity],
    );

    const confirm = useCallback(async () => {
        if (!pendingForm) return;

        await onConfirmSave(pendingForm, true);
        setPendingForm(null);
    }, [pendingForm, onConfirmSave]);

    const dismiss = useCallback(() => {
        setPendingForm(null);
    }, []);

    return {
        handleSave,
        confirm,
        dismiss,
        pendingForm,
        isOpen: !!pendingForm,
    };
}
